import { assertUnhandledCase } from "../common/asserts.common";
import type { FinancePaymentEntity } from "../models/finance_payment/finance_payment.model";
import type { PaymentGatewayEnum } from "./enums";

export interface PaymentGroup {
    id: string;
    label: string;
    enabled: boolean;
    paymentMethods: PaymentMethod[];
}

export interface PaymentMethod {
    id: string;
    icon: string;
    label: string;
    enabled: boolean;
    fee?: {
        fixed?: number;
        percentage?: number;
        tax: number;
    };
}

export interface PaymentInvoice {
    transactionId: string;
    paymentSessionId: string;
    paymentURL: string;
    expirationDate: Date;
    fee: number;
    raw: Record<string, unknown>;
    gateway: PaymentGatewayEnum.Values;
}

export const paymentGroups = {
    "virtual-accounts": ["BCA", "BNI", "BSI", "BRI", "MANDIRI", "PERMATA", "BNC" /* , "CIMB", "BJB" */],
    "e-wallets": ["OVO", "DANA", "SHOPEEPAY", "LINKAJA", "JENIUSPAY" /* "ASTRAPAY" */],
    "over-the-counter": ["ALFAMART", "INDOMARET"],
    "qris": ["QRIS"],
} as const;

export const paymentMethods = (() => {
    return Object.values(paymentGroups).flatMap((e) => e);
})();

export type PaymentGroupName = keyof typeof paymentGroups;
export type PaymentMethodName = (typeof paymentGroups)[PaymentGroupName][number];

export const groupLabels = {
    "e-wallets": "E-Wallet",
    "over-the-counter": "Minimarket",
    "virtual-accounts": "Virtual Account",
    "qris": "QRIS",
} satisfies Record<PaymentGroupName, string>;

export const methodLabels = {
    BCA: "BCA Virtual Account",
    BNI: "BNI Virtual Account",
    BSI: "BSI Virtual Account",
    BRI: "BRI Virtual Account",
    MANDIRI: "Mandiri Virtual Account",
    PERMATA: "Permata Virtual Account",
    BNC: "BNC Virtual Account",
    // CIMB: "CIMB Virtual Account",
    // BJB: "BJB Virtual Account",
    OVO: "OVO",
    DANA: "DANA",
    SHOPEEPAY: "ShopeePay",
    LINKAJA: "LinkAja",
    JENIUSPAY: "Jenius Pay",
    // ASTRAPAY: "AstraPay",
    ALFAMART: "Alfamart / Alfamidi",
    INDOMARET: "Indomaret",
    QRIS: "QRIS",
} satisfies Record<PaymentMethodName, string>;

export namespace XenditPaymentData {
    export interface InvoiceV2CreateRequest {
        external_id: string;
        amount: number;
        payment_methods: PaymentMethodName[];
        currency: "IDR";
        locale: "id";
        fees?: { type: string; value: number }[];
        items?: { name: string; quantity: number; price: number }[];
        success_redirect_url?: string;
    }

    export interface InvoiceV2CreateResponse {
        id: string;
        external_id: string;
        status: "PENDING" | "PAID" | "SETTLED" | "EXPIRED";
        amount: number;
        invoice_url: string;
        paid_amount?: number;
        expiry_date?: string;
        payment_method: "BANK_TRANSFER" | "RETAIL_OUTLET" | "EWALLET" | "QR_CODE";
        payment_channel: PaymentMethodName;
        fees?: {
            type: string;
            value: number;
        }[];
        [key: string]: unknown;
    }

    export interface InvoiceDeleteResponse {
        id: string;
        external_id: string;
        status: "EXPIRED";
    }

    export interface InvoiceV3CreateRequest {
        reference_id: string;
        session_type: "PAY";
        allow_save_payment_method: "DISABLED";
        currency: "IDR";
        amount: number;
        mode: "PAYMENT_LINK";
        capture_method: "AUTOMATIC";
        country: "ID";
        allowed_payment_channels: string[];
        locale: "id";
        metadata: { transactionId: string };
        items?: {
            reference_id: string;
            name: string;
            type: "DIGITAL_SERVICE" | "FEE";
            net_unit_amount: number;
            quantity: number;
            category: "DIGITAL_SERVICE" | "FEE";
        }[];
        success_return_url: string;
        expires_at?: string;
    }

    export interface InvoiceV3CreateResponse {
        payment_session_id: string;
        status: "ACTIVE" | "COMPLETED" | "EXPIRED" | "CANCELED";
        reference_id: string;
        amount: number;
        expires_at: string;
        success_return_url: string;
        metadata: { transactionId: string };
        payment_link_url: string;
    }

    export interface InvoiceV3Webhook {
        event: "payment_session.completed" | "payment_session.expired";
        api_version: "v1";
        data: {
            mode: "PAYMENT_LINK";
            amount: number;
            status: "ACTIVE" | "COMPLETED" | "EXPIRED" | "CANCELED";
            country: "ID";
            created: string;
            updated: string;
            currency: "IDR";
            expires_at: string;
            payment_id: string;
            reference_id: string;
            session_type: "PAY";
            payment_link_url: string;
            payment_request_id: string;
            payment_session_id: string;
            success_return_url: string;
            metadata: { transactionId: string };
        };
    }

    export interface InvoiceV3WebhookCapture {
        event: "payment.capture";
        api_version: "v3";
        data: {
            status: "SUCCEEDED" | "PENDING" | "FAILED" | "CANCELED" | "EXPIRED" | "AUTHORIZED";
            metadata: { transactionId: string };
        };
    }

    export interface TransactionResponse {
        has_more: boolean;
        data: {
            id: string;
            status: "SUCCESS";
            channel_category: string;
            channel_code: string;
            reference_id: string;
            amount: number;
            cashflow: "MONEY_IN" | "MONEY_OUT";
            settlement_status: "PENDING" | "SETTLED";
            fee: {
                xendit_fee: number;
                value_added_tax: number;
                status: "PENDING" | "COMPLETED" | "CANCELED" | "REVERSED";
            };
        }[];
    }

    export interface PaymentMethod {
        code: string;
        group: PaymentGroupName;
        calculationMethod: "percentage" | "fixed" | "percentage-and-fixed" | "percentage-or-fixed";
        fixed?: number;
        percentage?: number;
        tax: number;
        enabled?: boolean;
    }
}

export function translatePaymentMethod(payment: FinancePaymentEntity): string {
    let paymentMethod: string | undefined;

    switch (payment.method) {
        case "C":
            paymentMethod = "Tunai";
            break;
        case "X":
            paymentMethod = "Transfer Manual";
            break;
        case "O":
            paymentMethod = payment.paymentChannel;
            break;
        case "D":
            paymentMethod = "Agen Pembayaran";
            break;
        default:
            assertUnhandledCase(payment.method);
    }

    return paymentMethod ?? "Lainnya";
}
